import { AGENTOS_AVATAR_ROLES, type AgentOSAvatarRole } from './AgentOSAvatarRuntime.ts'

export const AGENTOS_AVATAR_MANIFEST_SCHEMA = 'agentos.avatar-manifest.v2' as const

export interface AgentOSAvatarRendererProfileV2 {
  crossfade_ms: number
  easing: string
  steady_state_decoders: number
  transition_decoders: number
  supported_frame_rates: number[]
}

export interface AgentOSAvatarCanonicalFrameV2 {
  url: string
  poster: string
  sha256: string
  width: number
  height: number
  aspect_ratio: string
  opacity_mode: 'opaque' | 'chroma_key'
  key_color: string | null
}

export interface AgentOSAvatarGuaranteesV2 {
  codec_profile: 'h264_high'
  pixel_format: 'yuv420p'
  frame_rate: number
  silent: true
  fast_start: true
  burned_in_fade: false
  constant_framing: true
  loop_diff_mean_max: number
  loop_diff_review_max: number
}

export interface AgentOSAvatarAssetV2 {
  role: AgentOSAvatarRole
  url: string
  poster: string
  loop: boolean
  duration: number
  sha256: string
  byte_size: number
  codec_profile: string
  pixel_format: string
  width: number
  height: number
  frame_rate: number
  audio_track_count: number
  fast_start: boolean
  burned_in_fade: boolean
  loop_diff_mean: number
  loop_diff_max: number
  storage_id?: number
}

export interface AgentOSAvatarManifestV2 {
  schema: typeof AGENTOS_AVATAR_MANIFEST_SCHEMA
  manifest_revision: string
  character_id: string
  variant_id: string
  canonical_frame: AgentOSAvatarCanonicalFrameV2
  guarantees: AgentOSAvatarGuaranteesV2
  renderer_profiles: Partial<Record<'web' | 'ios', AgentOSAvatarRendererProfileV2>>
  assets: Record<AgentOSAvatarRole, AgentOSAvatarAssetV2>
  keying?: {
    metric: 'g-max-rb'
    threshold_opaque: number
    threshold_transparent: number
    despill: 'cap-g-to-max-rb'
  }
}

export interface AgentOSAvatarManifestValidation {
  manifest: AgentOSAvatarManifestV2
  warnings: Array<{
    code: 'loop_peak_requires_review'
    role: AgentOSAvatarRole
    measured: number
    threshold: number
  }>
}

export class AgentOSAvatarManifestViolation extends Error {
  readonly code: string

  constructor(code: string, detail?: string) {
    super(detail ? `${code}: ${detail}` : code)
    this.name = 'AgentOSAvatarManifestViolation'
    this.code = code
  }
}

const SHA256_PATTERN = /^[0-9a-fA-F]{64}$/

function object(value: unknown, code: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new AgentOSAvatarManifestViolation(code)
  }
  return value as Record<string, unknown>
}

function httpsUrl(value: unknown): boolean {
  if (typeof value !== 'string') return false
  try { return new URL(value).protocol === 'https:' } catch { return false }
}

function validRendererProfile(value: unknown): value is AgentOSAvatarRendererProfileV2 {
  const profile = object(value, 'invalid_renderer_profile')
  return typeof profile.crossfade_ms === 'number' && profile.crossfade_ms > 0
    && typeof profile.easing === 'string' && profile.easing.length > 0
    && typeof profile.steady_state_decoders === 'number' && profile.steady_state_decoders > 0
    && typeof profile.transition_decoders === 'number'
    && profile.transition_decoders >= profile.steady_state_decoders
    && Array.isArray(profile.supported_frame_rates)
    && profile.supported_frame_rates.length > 0
    && profile.supported_frame_rates.every((fps) => typeof fps === 'number' && fps > 0)
}

/**
 * Parse and validate a v2 manifest. A caller may activate the returned
 * manifest only if this function succeeds; missing QA metadata never falls
 * back to the legacy eight-role package.
 */
export function validateAgentOSAvatarManifestV2(
  input: unknown,
  target: 'shared' | 'web' | 'ios' = 'shared',
): AgentOSAvatarManifestValidation {
  const root = object(input, 'manifest_not_object')
  if (root.schema !== AGENTOS_AVATAR_MANIFEST_SCHEMA) {
    throw new AgentOSAvatarManifestViolation('unsupported_schema', String(root.schema))
  }
  if (![root.manifest_revision, root.character_id, root.variant_id]
    .every((value) => typeof value === 'string' && value.length > 0)) {
    throw new AgentOSAvatarManifestViolation('missing_identity')
  }

  const canonical = object(root.canonical_frame, 'invalid_canonical_frame')
  if (!httpsUrl(canonical.url) || !httpsUrl(canonical.poster)
    || typeof canonical.sha256 !== 'string' || !SHA256_PATTERN.test(canonical.sha256)
    || typeof canonical.width !== 'number' || canonical.width <= 0
    || typeof canonical.height !== 'number' || canonical.height <= 0
    || typeof canonical.aspect_ratio !== 'string'
    || (canonical.opacity_mode !== 'opaque' && canonical.opacity_mode !== 'chroma_key')
    || (canonical.opacity_mode === 'chroma_key'
      && (typeof canonical.key_color !== 'string' || canonical.key_color.length === 0))) {
    throw new AgentOSAvatarManifestViolation('invalid_canonical_frame')
  }

  const guarantees = object(root.guarantees, 'unsupported_guarantees')
  if (guarantees.codec_profile !== 'h264_high'
    || guarantees.pixel_format !== 'yuv420p'
    || typeof guarantees.frame_rate !== 'number' || guarantees.frame_rate <= 0
    || guarantees.silent !== true || guarantees.fast_start !== true
    || guarantees.burned_in_fade !== false || guarantees.constant_framing !== true
    || typeof guarantees.loop_diff_mean_max !== 'number'
    || guarantees.loop_diff_mean_max <= 0 || guarantees.loop_diff_mean_max > 2
    || typeof guarantees.loop_diff_review_max !== 'number'
    || guarantees.loop_diff_review_max <= guarantees.loop_diff_mean_max) {
    throw new AgentOSAvatarManifestViolation('unsupported_guarantees')
  }

  const profiles = object(root.renderer_profiles, 'missing_renderer_profile')
  const requiredTargets = target === 'shared' ? ['web', 'ios'] as const : [target] as const
  if (requiredTargets.some((requiredTarget) => !validRendererProfile(profiles[requiredTarget]))) {
    throw new AgentOSAvatarManifestViolation('invalid_renderer_profile')
  }
  if (profiles.web !== undefined && !validRendererProfile(profiles.web)) {
    throw new AgentOSAvatarManifestViolation('invalid_renderer_profile', 'web')
  }
  if (profiles.ios !== undefined && !validRendererProfile(profiles.ios)) {
    throw new AgentOSAvatarManifestViolation('invalid_renderer_profile', 'ios')
  }
  const ios = profiles.ios as AgentOSAvatarRendererProfileV2 | undefined
  if (ios && (ios.steady_state_decoders !== 1 || ios.transition_decoders !== 2)) {
    throw new AgentOSAvatarManifestViolation('invalid_renderer_profile', 'ios decoder lifecycle')
  }
  for (const profile of [profiles.web, profiles.ios]) {
    if (profile && !profile.supported_frame_rates.includes(guarantees.frame_rate as number)) {
      throw new AgentOSAvatarManifestViolation('unsupported_frame_rate')
    }
  }

  const assets = object(root.assets, 'role_set_mismatch')
  const suppliedRoles = Object.keys(assets).sort()
  const requiredRoles = [...AGENTOS_AVATAR_ROLES].sort()
  if (JSON.stringify(suppliedRoles) !== JSON.stringify(requiredRoles)) {
    throw new AgentOSAvatarManifestViolation(
      'role_set_mismatch',
      `required=${requiredRoles.join(',')} supplied=${suppliedRoles.join(',')}`,
    )
  }

  const warnings: AgentOSAvatarManifestValidation['warnings'] = []
  for (const role of AGENTOS_AVATAR_ROLES) {
    const asset = object(assets[role], `invalid_asset:${role}`)
    if (asset.role !== role || !httpsUrl(asset.url) || !httpsUrl(asset.poster)
      || typeof asset.duration !== 'number' || asset.duration <= 0
      || typeof asset.sha256 !== 'string' || !SHA256_PATTERN.test(asset.sha256)
      || typeof asset.byte_size !== 'number' || asset.byte_size <= 0) {
      throw new AgentOSAvatarManifestViolation('invalid_asset', role)
    }
    if (asset.codec_profile !== guarantees.codec_profile
      || asset.pixel_format !== guarantees.pixel_format
      || asset.width !== canonical.width || asset.height !== canonical.height
      || asset.frame_rate !== guarantees.frame_rate
      || asset.audio_track_count !== 0 || asset.fast_start !== true
      || asset.burned_in_fade !== false) {
      throw new AgentOSAvatarManifestViolation('inconsistent_asset', role)
    }
    const shouldLoop = role !== 'greeting'
    if (asset.loop !== shouldLoop
      || typeof asset.loop_diff_mean !== 'number' || asset.loop_diff_mean < 0
      || asset.loop_diff_mean > (guarantees.loop_diff_mean_max as number)
      || typeof asset.loop_diff_max !== 'number' || asset.loop_diff_max < 0
      || asset.loop_diff_max > 255) {
      throw new AgentOSAvatarManifestViolation('invalid_loop', role)
    }
    if ((asset.loop_diff_max as number) > (guarantees.loop_diff_review_max as number)) {
      warnings.push({
        code: 'loop_peak_requires_review',
        role,
        measured: asset.loop_diff_max as number,
        threshold: guarantees.loop_diff_review_max as number,
      })
    }
  }

  return {
    manifest: root as unknown as AgentOSAvatarManifestV2,
    warnings,
  }
}
